<template>
    <div id="ql-app" @mousemove="selectMouseCoordinates">
        <!-- Read-only communication: the editor silently disabled tools before; this
             makes the read-only state explicit for viewers who lack edit rights
             (non-paying collaborators, lapsed users, or a project not unlocked). -->
        <div
            v-if="loaded && !canEditActiveProject"
            class="ql-readonly-banner"
            style="position:fixed;bottom:0;left:0;right:0;z-index:2000;background:#37474f;color:#fff;text-align:center;padding:6px 12px;font-size:13px;line-height:1.3;box-shadow:0 -2px 6px rgba(0,0,0,0.25);"
        >
            You have <strong>read-only</strong> access to this project.
            <use-show-pass variant="bar"></use-show-pass>
        </div>
        <md-dialog
            ref="bannerDialog"
            :md-click-outside-to-close="false"
            :md-esc-to-close="false"
            class="alert-banner-dialog"
        >
            <md-dialog-content class="alert-content" v-if="editedUser">
                <div class="alert-box">
                    <div class="alert-icon-box">
                        <md-icon class="alert-icon">warning</md-icon>
                    </div>
                    <div class="alert-text-box">
                        <h3 class="alert-title">Hold, please!</h3>
                        <p class="alert-message">
                            <span style="font-weight: bold;">{{ this.editedUser || 'A collaborator' }}</span> is currently adding, deleting, or replacing pdf pages in this project. Please wait until the action is completed or canceled.
                            <span v-if="user && project.ownerUID === user.uid" style="display: block; margin-top: 8px;">As the owner, you can take over by clicking the close button below.</span>
                        </p>
                    </div>
                </div>
            </md-dialog-content>
            <md-dialog-actions v-if="user && project.ownerUID === user.uid">
                <md-button class="md-primary" @click="closeAlert">Close anyway</md-button>
            </md-dialog-actions>
        </md-dialog>
        <div class="header-main">
            <project-header
                v-if="loaded"
                :user="user"
                :currentPage="currentPage" 
                :locked="isLocked" 
                :showMode="showMode"
                :showMarginView="showMarginView"
                :bookmarkNavOpened="bookmarkNavOpened"
                :listView="listView"
                :selectedPageRenameIndices="selectedPageRenameIndices"
                :thumbnailPage="thumbnailPage || currentPage"
                :thumbnailStep="thumbnailPerRow"
                @pageUp="pageUp" 
                @pageDown="pageDown"
                @setPage="setCurrentPage"
                @setThumbnailPage="goToThumbnailPage"
                @toggleShowMode="toggleShowMode"
                @toggleMarginView="toggleMarginView"
                @toggleLocked="toggleLocked"
                @toggleBookmarkNav="toggleBookmarkNav"
                @toggleListView="toggleListView"
            />
            <project-sub-header
                v-if="loaded && !listView"
                :user="user"
                :currentPage="currentPage" 
                :locked="getLockedStatus()" 
                :showMode="showMode"
                :bookmarkNavOpened="bookmarkNavOpened"
                :listView="listView"
                :selectedElement="selectedElement"
                :historyIndex="historyIndex"
                :history="history"
                :selectedPageRenameIndices="selectedPageRenameIndices"
                :pageHeight="pageHeight"
                :pageWidth="pageWidth"
                :pageFollow="pageFollowState"
                @thumbnailSelectionClick="thumbnailSelectionClick"
                @setScale="setScale"
                @updateHistory="updateHistory"
                @setSelectedElement="setSelectedElement"
                @pasteCueNote="pasteCueNote"
                @pageUp="pageUp"
                @pageDown="pageDown"
                @setPage="setCurrentPage"
                @setFollowUid="setFollowUid"
                @toggleLocked="toggleLocked"
                @toggleBookmarkNav="toggleBookmarkNav"
                @toggleListView="toggleListView"
                @copyElement="copyElement"
            />
        </div>
        <core-nav-main
            :currentPage="currentPage"
            :showMode="showMode"
            :locked="isLocked"
            @toggleLocked="toggleLocked"
            v-if="loaded"
        />
        <project-worknote-cues-nav />
        <md-layout class="page-viewer pdf-thumbnail" tabindex="0" v-if="loaded && selectedThumbnailView === 'true'">
            <md-layout>
                <project-thumbail-pages
                    ref="thumbnailPages"
                    :key="thumbnailKey"
                    :bookmarkNavOpened="bookmarkNavOpened"
                    :currentPage="currentPage % 2 === 0 ? currentPage - 1 : currentPage > 1 ? currentPage : currentPage"
                    :startPage="thumbnailPage || currentPage"
                    :locked="getLockedStatus()"
                    :showMode="showMode"
                    :totalPages="totalPages"
                    @toggleBookmarkNav="toggleBookmarkNav"
                    @thumbnailSelectionClick="thumbnailSelectionClick"
                    @thumbnailPageChanged="thumbnailPage = $event"
                    @perRowChanged="thumbnailPerRow = $event"
                    :selectedPageRenameIndices="selectedPageRenameIndices"
                    @gotoPage="gotoPage"
                />
                <project-list-view
                    v-if="listView"
                    :currentPage="currentPage % 2 === 0 ? currentPage - 1 : currentPage > 1 ? currentPage : currentPage"
                    @gotoPage="gotoPage"
                    @switchViewToPage="switchViewToPage"
                />
            </md-layout>
        </md-layout>
        <md-layout class="page-viewer" :class="{ 'split-view': inSplitView, 'split-dragging': splitDragging }" :style="inSplitView ? { '--pdf-frac': splitRatio } : {}" v-else-if="loaded">
            <project-bookmark-nav
                :bookmarkNavOpened="bookmarkNavOpened"
                :activeBookmarkTab="activeBookmarkTab"
                :focusBookmarkPage="focusBookmarkPage"
                :currentPage="currentPage % 2 === 0 ? currentPage - 1 : currentPage > 1 ? currentPage : currentPage"
                @toggleBookmarkNav="toggleBookmarkNav"
                @gotoPage="gotoPage"
                @setBookmarkTab="setBookmarkTab"
                @clearBookmarkFocus="clearBookmarkFocus"
            />
            <project-pages v-if="!reloading"
                :bookmarkNavOpened="bookmarkNavOpened"
                :currentPage="(selectedPageType === 'no' || ( selectedPageType === 'even' )) ? currentPage : currentPage % 2 === 0 ? currentPage - 1 : currentPage > 1 ? currentPage : currentPage"
                :locked="getLockedStatus()"
                :showMode="showMode"
                :showMarginView="showMarginView"
                :selectedElement="selectedElement"
                @setSelectedElement="setSelectedElement"
                @copyElement="copyElement"
                @updatePageHistory="updatePageHistory"
                :totalPages="totalPages"
                :pageHeight="pageHeight"
                @toggleBookmarkNav="toggleBookmarkNav"
                @openBookmarkNav="openBookmarkNav"
                @setBookmarkTab="setBookmarkTab"
                @gotoPage="gotoPage"
                @getPageWidth="getPageWidth"
                @getPageHeight="getPageHeight"
            />
            <div v-if="inSplitView" class="ql-split-divider" @mousedown="onSplitDragStart" @touchstart="onSplitDragStart">
                <span class="ql-split-grip"></span>
            </div>
            <project-list-view
                v-if="listView || inSplitView"
                :currentPage="inSplitView ? currentPage : (currentPage % 2 === 0 ? currentPage - 1 : currentPage > 1 ? currentPage : currentPage)"
                :splitView="inSplitView"
                :pageSync="splitPageSync === 'true'"
                @gotoPage="gotoPage"
                @switchViewToPage="switchViewToPage"
            />
        </md-layout>
        <core-footer></core-footer>
    </div>
</template>

<script>
import Vue from 'vue';
import { pushStep } from '@/lib/note-history';
import { userOptionsForPaste, isTypingTarget, isCuePress, isMarkPress, pasteSpotFrom, resolvePasteSpot } from '@/lib/cue-paste';
import PDFJS from 'pdfjs-dist/webpack';
import firebase from 'firebase/app';
import axios from 'axios';
import { db } from '@/main';
import { mapGetters } from 'vuex';
import NavBus from '@/buses/nav';
import CoreNavMain from '@/components/core/nav/main';
import CoreHeader from '@/components/core/header';
import CoreFooter from '@/components/core/footer';
import ProjectHeader from '@/components/project/header';
import ProjectSubHeader from '@/components/project/sub-header';
import ProjectBookmarkNav from '@/components/project/bookmark/nav';
import WorkNoteCuesNav from '@/components/project/worknote/nav';
import ProjectPages from '@/components/project/pages';
import ProjectThumbnailPages from '@/components/project/thumbnail-pages';
import ProjectListView from '@/components/project/list-view';
import UseShowPass from '@/components/show-pass/use-show-pass';
import startPageFollowSession from '@/lib/pageFollow';
import { initEditingGuard, teardownEditingGuard, isActivelyEditing } from '@/lib/editingGuard';
import { resolvePageIndexFromDeepLink } from '@/lib/resolve-page-index';
import { healPageLabels, extendedPageLabels } from '@/lib/page-labels';
import { viewingToSeedOnOpen } from '@/lib/layer-visibility-on-open';

import VueClipboard from 'vue-clipboard2';

Vue.use(VueClipboard);

PDFJS.workerSrc = '../../../node_modules/pdfjs-dist/build/pdf.worker.js';

// reload() waits for the project's documentUrl to appear after the vuexfire
// bindings settle. 40 x 100ms = ~4 seconds, which is generous for a bind and
// still finite. Before this, the wait was unbounded and ran at frame rate.
const RELOAD_MAX_ATTEMPTS = 40;
const RELOAD_RETRY_MS = 100;

const ProjectView = {
    name: 'project',
    props: ['id', 'version'],
    data: function() {
        return {
            currentPage: 1,
            theme: 'default',
            loaded: false,
            reloading: false,
            printMode: false,
            readyUser: false,
            readyProject: false,
            readyVersion: false,
            bookmarkNavOpened: false,
            activeBookmarkTab: 'structure',
            focusBookmarkPage: null,
            listView: false,
            splitDragging: false,
            prevSplitView: null,
            wheelTimerID: null,
            wheelDirection: '',
            selectedElement: null,
            copiedElement: null,
            selectedCue: null,
            copiedCue: null,
            copiedCueSettings: null,
            pastingCue: false,
            selectedMark: null,
            copiedMark: null,
            pastingMark: false,
            lastPasteSpot: null,
            history: [],
            historyIndex: 0,
            selectedPageRenameIndices: [],
            pageHeight: 0,
            pageWidth: 0,
            eventX: 0,
            eventY: 0,
            thumbnailKey: 0,
            // The thumbnail view's OWN reading position, remembered here because
            // the component lives behind a v-if and is destroyed on every switch
            // to PDF view and on every page operation (thumbnailKey bump).
            //
            // ⚠ Deliberately NOT currentPage. That is Page Mirror's broadcast
            // channel -- writing thumbnail scrolling into it would drag every
            // follower's view around as the leader scrolls thumbnails. Seeded
            // FROM currentPage the first time the view opens, then independent.
            thumbnailPage: null,
            // Thumbnails per row, measured by the grid and reported up. Drives
            // the page arrows: one press moves one ROW, which is what makes them
            // feel responsive in a grid. Changes with the browser width.
            thumbnailPerRow: null,
            animationFrame: null,
            unloadTimer: null,
            editedUser: null,
            // Mirror (pageFollow). followUid is the single source of truth for
            // "who am I following" — null means off. followRemoteApply carries the
            // exact page value a non-user write is about to land on, so the
            // currentPage watcher can tell "the leader moved me" from "I moved
            // myself" without any call site having to say which it was.
            followUid: null,
            followRemoteApply: null,
            followPaused: false,
            followOutOfRange: null,
            followResumeTimer: null,
            pageFollowPeers: {},
            pfHandle: null,
            resolvedDeepLinkCueId: null
        };
    },
    computed: {
        ...mapGetters([
            'project',
            'selectedThumbnailView',
            'selectedTool',
            'documentUrl',
            'isLocked',
            'selectedPageType',
            'user',
            'showMode',
            'showCuesLayerIDs',
            'showMarginView',
            'currentLayer',
            'visibleLayerIDs',
            'currentNoteLayer',
            'selectedScale',
            'showThumbnailPageUpdate',
            'currentVersion',
            'projectTotalPages',
            'projectBannerDisplay',
            'projectID',
            'projectBannerUser',
            'userByUID',
            'splitView',
            'splitPageSync',
            'splitRatio',
            'activeProjectEntitled',
            'canEditActiveProject',
            'cues'
        ]),
        inSplitView: function() {
            // Split view is a single-page-PDF mode; it must not collide with the
            // thumbnail grid. Was behind the 'splitView' early-adopter flag until
            // it came off; split view is now available to everyone.
            return this.splitView === 'true' && this.selectedThumbnailView === 'false';
        },
        projectVersion: function() {
            return this.currentVersion ? this.currentVersion.id : '';
        },
        // Mirror applies only while a PDF is on screen. inSplitView already
        // implies thumbnails are off, and the listView watcher forces split off,
        // so this one expression covers single, split, list and thumbnail.
        isPdfView: function() {
            return this.selectedThumbnailView === 'false' && !this.listView;
        },
        followedPeer: function() {
            return (this.followUid && this.pageFollowPeers[this.followUid]) || null;
        },
        // One object down to the toolbar rather than four props, since these three
        // values are only ever read together.
        pageFollowState: function() {
            return {
                uid: this.followUid,
                peers: this.pageFollowPeers,
                paused: this.followPaused,
                outOfRange: this.followOutOfRange
            };
        },
        totalPages: function() {
            if (this.projectVersion) {
                return this.projectTotalPages || 0;
            }

            if (this.project.pdfPageNumbers && this.project.pdfPageNumbers.length === 0) {
                return this.projectTotalPages || 0;
            }
            
            return (this.project && Array.isArray(this.project.pdfPageNumbers)) 
                ? this.project.pdfPageNumbers.length 
                : 0;
        }
    },
    watch: {
        // Mirror watchers are declared FIRST, and `loaded` before `currentPage`,
        // deliberately. Vue 2 flushes watchers in declaration order, and
        // initDocument sets `loaded = true` and then synchronously restores the
        // page — both land in the same flush. Re-arm must not run behind a
        // currentPage handler that already tore the follow down.
        loaded: function(val) {
            if (!val) return;
            this.startPageFollow();
            this.applyRemotePage(this.leaderPage());
        },
        // A saved version mounts this same component, where "page 7" means a
        // different page than it does live. Never broadcast from, or follow into, a
        // version view.
        projectVersion: function(val) {
            if (val) {
                this.stopPageFollow();
            } else if (this.loaded) {
                this.startPageFollow();
            }
        },
        totalPages: function() {
            this.applyRemotePage(this.leaderPage());
        },
        isPdfView: function(val) {
            // The peers listener only fires on change, so a follower who sat in
            // list view while the leader moved 5 -> 9 -> 12 has heard nothing.
            // Re-apply the leader's latest page on the way back in.
            if (val) this.applyRemotePage(this.leaderPage());
        },
        currentPage: function(newVal) {
            const applied = this.followRemoteApply;
            if (this.followUid && applied !== newVal) {
                // Nobody claimed this write, so the user made it. That is the one
                // thing that means "stop following".
                this.breakFollow('you changed the page');
            }
            // Broadcast unconditionally, including pages we applied from a leader.
            // It cannot loop (an unchanged value never fires this watcher), and
            // suppressing it would strand anyone following US on a stale page.
            if (this.pfHandle) this.pfHandle.publishPage();
            // Remember where the live doc was left, per-project and per-device, so
            // reopening lands here instead of page 1. Not for version views — "page
            // 7" of a saved snapshot is a different page than live.
            if (!this.projectVersion && this.id && newVal >= 1) {
                localStorage.setItem(this.lastPageKey(), String(newVal));
            }
        },
        inSplitView: function(val) {
            // Split view and the full-screen list overlay are mutually exclusive;
            // entering split clears the lingering listView flag.
            if (val && this.listView) {
                this.listView = false;
            }
        },
        listView: function(val) {
            if (val) {
                // Entering the full list-only view: remember the split state and
                // force list-only, so there's no split option while in list view.
                this.prevSplitView = this.splitView;
                if (this.splitView === 'true') {
                    this.$store.dispatch('setSplitView', { value: 'false' });
                }
            } else if (this.prevSplitView === 'true') {
                // Returning to the PDF view: restore the previous (split) view.
                this.$store.dispatch('setSplitView', { value: 'true' });
                this.prevSplitView = null;
            }
        },
        documentUrl: function() {
        },
        'project.collaborators': function() {
            this.checkAccessible('project.collaborators');
        },
        'project.ownerUID': function() {
            this.checkAccessible('project.ownerUID');
        },
        'user.projects': function() {
            this.checkAccessible('user.projects');
        },
        version: function() {
            this.$store.dispatch('useVersion', {
                versionID: this.version || null,
                callback: () => {
                }
            });
        },
        showMode: {
            immediate: true,
            handler: function(newVal) {
                this.theme = newVal ? 'show-mode' : 'default';
                Vue.material.setCurrentTheme(this.theme);
            }
        },
        showThumbnailPageUpdate(newVal) {
            if (newVal) {
                // Reload where they were working, not at page 1. Deleting a page
                // from halfway down a 200-page script used to throw the user back
                // to the top -- the key bump discards the component's scroll
                // state, and this call then explicitly asked for page 1.
                this.thumbnailKey += 1;
                this.reload(this.thumbnailPage || this.currentPage || 1, 'showThumbnailPageUpdate');
            }
        },
        projectBannerUser: {
            async handler(newVal) {
                if (newVal) {
                    const user = await this.userByUID(newVal);
                    this.editedUser = user ? (user.username || '') : '';
                } else {
                    this.editedUser = '';
                }
            },
            immediate: true
        },
        projectBannerDisplay: {
            async handler(newVal) {
                if (newVal && firebase.auth().currentUser && (this.project.projectBannerUser !== firebase.auth().currentUser.uid)) {
                    this.$nextTick(() => {
                        if (this.$refs.bannerDialog) {
                            this.$refs.bannerDialog.open();
                        }
                    });
                } else {
                    this.$nextTick(() => {
                        if (this.$refs.bannerDialog) {
                            this.$refs.bannerDialog.close();
                        }
                    });
                }
            },
            immediate: true
        },
        // Another collaborator changed the PDF page structure (replace/insert/delete/
        // reorder). renamePdfPages bumps pdfReloadTick once, at commit; reload the PDF
        // for THIS viewer (the initiator skips). This is a single, reliable signal,
        // unlike the multi-toggle projectBannerDisplay flag.
        'project.pdfReloadTick': function(newVal, oldVal) {
            const me = firebase.auth().currentUser;
            const by = this.project && this.project.pdfReloadBy;
            const initiatedByOther = by && me && (by !== me.uid);
            if (this.loaded && newVal && newVal !== oldVal && initiatedByOther) {
                this.refreshPdfAfterRemotePageOp();
            }
        },
        '$route.query': {
            handler() {
                this.resolvedDeepLinkCueId = null;
                this.$nextTick(() => this.handleInboundDeepLink());
            },
            deep: true,
            immediate: true
        },
        cues() {
            const cueId = this.$route && this.$route.query && this.$route.query.cue;
            if (cueId && cueId !== this.resolvedDeepLinkCueId) {
                this.handleInboundDeepLink();
            }
        }
    },
    methods: {
        resolveInitialPageFromRoute() {
            const cueId = this.$route && this.$route.query && this.$route.query.cue;
            const page = this.$route && this.$route.query && this.$route.query.page;
            if (cueId && this.$store.getters.cues) {
                const cue = this.$store.getters.cues.find(c => c.id === cueId);
                if (cue && cue.pageNumber) {
                    return cue.pageNumber;
                }
            }
            if (page) {
                const pageIndex = resolvePageIndexFromDeepLink(
                    page,
                    this.project && this.project.pdfPageNumbers
                );
                if (pageIndex) {
                    return pageIndex;
                }
            }
            return 1;
        },
        handleInboundDeepLink() {
            const cueId = this.$route && this.$route.query && this.$route.query.cue;
            const page = this.$route && this.$route.query && this.$route.query.page;
            if (cueId && this.$store.getters.cues) {
                const cue = this.$store.getters.cues.find(c => c.id === cueId);
                if (cue && cue.pageNumber) {
                    this.currentPage = cue.pageNumber;
                    NavBus.$emit('goToPage', cue.pageNumber);
                    this.setSelectedElement({ type: 'cue', cue });
                    this.resolvedDeepLinkCueId = cueId;
                    return;
                }
            }
            if (page) {
                const pageIndex = resolvePageIndexFromDeepLink(
                    page,
                    this.project && this.project.pdfPageNumbers
                );
                if (pageIndex) {
                    this.currentPage = pageIndex;
                    NavBus.$emit('goToPage', pageIndex);
                }
            }
        },
        selectMouseCoordinates(event) {
            this.eventX = event.clientX;
            this.eventY = event.clientY;
        },
        checkAccessible() {
            if (!this.project) return;
            if (!this.readyUser && !this.readyProject) return;
            if (!this.project.id) return;
            if (!this.project.ownerUID) return;
            const inUserList = !!(this.user && Object.keys(this.user.projects || {}).includes(this.project.id));
            const onProject = !!(this.user && (this.project.ownerUID === this.user.uid || Object.keys(this.project.collaborators || {}).includes(this.user.uid)));
            if (inUserList && onProject) {
                return;
            }
            // Say which half failed. Josh hit a one-off bounce back to the dashboard
            // right after accepting an invite (23 Sept 2026), and two different exits
            // on this page look identical from the outside.
            console.warn('[project] leaving: not accessible', {
                projectId: this.project.id, inUserList, onProject
            });
            this.$router.replace('/dashboard');
        },
        toggleShowMode() {
            this.$store.dispatch('updateShowMode', !this.showMode);
            if (this.showMode) {
                this.theme = 'show-mode';
            } else {
                this.theme = 'default';
            }
            Vue.material.setCurrentTheme(this.theme);
        },
        toggleMarginView() {
            this.$store.dispatch('updateMarginView', {
                fieldKey: 'showMargin',
                value: !this.showMarginView
            });
        },
        toggleLocked() {
            this.$store.dispatch('lockProject', !this.isLocked);
        },
        toggleBookmarkNav() {
            if (this.bookmarkNavOpened) {
                this.bookmarkNavOpened = false;
            } else {
                this.activeBookmarkTab = 'structure';
                this.bookmarkNavOpened = true;
            }
        },
        openBookmarkNav(tab = 'structure', focusPage = null) {
            this.activeBookmarkTab = tab;
            this.focusBookmarkPage = focusPage;
            if (!this.bookmarkNavOpened) {
                this.bookmarkNavOpened = true;
            }
        },
        setBookmarkTab(tab) {
            this.activeBookmarkTab = tab;
        },
        clearBookmarkFocus() {
            this.focusBookmarkPage = null;
        },
        setSelectedElement(data) {
            this.selectedElement = data;
            this.selectedCue = null;
            this.selectedMark = null;
        },
        copyElement() {
            if (this.selectedElement) {
                this.copiedElement = this.selectedElement;
                this.copiedCue = null;
                this.copiedMark = null;
            }
        },
        // Cue copy/paste (22 Oct 2026, ~/Code/CUELIST_CUE_COPY_PASTE_2026-10-22.md): the last thing pressed, a cue or a
        // note, is what Cmd-C copies.
        onCueSelected(cue) {
            this.selectedCue = cue;
            this.selectedMark = null;
            this.selectedElement = null;
        },
        onMarkSelected(mark) {
            this.selectedMark = mark;
            this.selectedCue = null;
            this.selectedElement = null;
        },
        copySelectedMark() {
            this.copiedMark = JSON.parse(JSON.stringify(this.selectedMark));
            this.copiedCue = null;
            this.copiedElement = null;
            this.$toast.success('Structure mark copied');
        },
        pasteCopiedMark() {
            if (this.pastingMark) return;
            const spot = resolvePasteSpot(this.lastPasteSpot, document);
            if (!spot) return;
            this.pastingMark = true;
            let claimed = false;
            NavBus.$emit('paste-structure-mark', {
                mark: this.copiedMark,
                clientX: spot.clientX,
                clientY: spot.clientY,
                claim: () => { claimed = true; },
                done: () => { this.pastingMark = false; }
            });
            if (!claimed) this.pastingMark = false;
        },
        copySelectedCue() {
            this.copiedCue = JSON.parse(JSON.stringify(this.selectedCue));
            this.copiedCueSettings = JSON.parse(JSON.stringify(this.$store.getters.userProjectData || {}));
            this.copiedElement = null;
            this.copiedMark = null;
            this.$toast.success('Cue copied');
        },
        pasteCopiedCue() {
            if (this.pastingCue) return;
            const click = resolvePasteSpot(this.lastPasteSpot, document);
            if (!click) return;
            this.pastingCue = true;
            let claimed = false;
            const fromId = this.copiedCue.id;
            const settings = this.copiedCueSettings;
            NavBus.$emit('paste-cue', {
                cue: this.copiedCue,
                clientX: click.clientX,
                clientY: click.clientY,
                claim: () => { claimed = true; },
                done: (cueData) => {
                    this.pastingCue = false;
                    if (!cueData) return;
                    // Your own look for the copied cue, onto the new one, in your own user doc only.
                    const update = userOptionsForPaste(settings, this.$store.getters.project.id, fromId, cueData.id);
                    if (Object.keys(update).length) this.$store.dispatch('copyCueLook', update);
                }
            });
            // The pages answer synchronously whether the click was on them; none did, so nothing is in flight.
            if (!claimed) this.pastingCue = false;
        },
        setScale(type) {
            this.$store.dispatch('setScale', {
                type
            });

            this.reloading = true;
            if (this.totalPages > 1) {
                this.lastPage = this.currentPage;

                NavBus.$emit('goToPage', this.currentPage);         
                setTimeout(() => {
                    this.reloading = false;
                }, 100);
            } else {
                this.reload(1, 'setScale');
            }
        },
        setPrintMode(val) {
            this.printMode = val;
        },
        setCurrentPage(number) {
            this.history = [];
            this.currentPage = Math.min(Math.max(number, 1), this.totalPages);
        },
        pageUp() {
            if (this.currentPage < this.totalPages) {
                this.setCurrentPage(this.currentPage + 1);
            }
        },
        pageDown() {
            if (this.currentPage > 1) {
                this.setCurrentPage(this.currentPage - 1);
            }
        },
        gotoPage(e) {
            this.setCurrentPage(e);
        },
        // The single way to move the page WITHOUT it counting as "the user changed
        // the page". Used for remote applies and for programmatic restores
        // (document reload, remote page-op refresh) — neither is a user action, so
        // neither may tear down an engaged follow.
        //
        // We claim a value, not a boolean: Vue 2 flushes watchers async, so if the
        // user clicks page 3 in the same tick a leader's page 7 arrives, the watcher
        // fires once with 7 and a boolean "is remote" flag would swallow the click.
        //
        // ORDER IS LOAD-BEARING. Vue queues nextTick callbacks in call order, and
        // the watcher flush is itself a nextTick callback queued by the mutation.
        // The clear MUST be queued after setCurrentPage, or it runs first, the
        // watcher finds no claim, and the very first remote apply breaks its own
        // follow.
        setCurrentPageProgrammatic(number) {
            // Mirror setCurrentPage's clamp exactly. A claim that doesn't match the
            // value that actually lands reads as a user action.
            this.followRemoteApply = Math.min(Math.max(number, 1), this.totalPages);
            this.setCurrentPage(number);
            this.$nextTick(() => {
                this.followRemoteApply = null;
            });
        },
        leaderPage() {
            const peer = this.followedPeer;
            return (peer && Number.isFinite(peer.page)) ? peer.page : null;
        },
        lastPageKey() {
            return `ql-last-page-${this.id}`;
        },
        // The page to open the live doc on: whatever this device last left it on,
        // clamped into range in case the document shrank. setCurrentPage clamps
        // again, but reading a wild stored value here would be confusing to debug.
        rememberedPage() {
            const raw = Number.parseInt(localStorage.getItem(this.lastPageKey()), 10);
            return Number.isFinite(raw) && raw >= 1 ? raw : 1;
        },
        // Snap the leader's page onto THIS viewer's spread grid. Leader and follower
        // can be in different page-type modes; each renders the spread containing the
        // page. Aligning also keeps pagination's +/-2 arithmetic correct afterwards.
        alignToSpread(page) {
            if (this.selectedPageType === 'odd') {
                return page % 2 === 0 ? Math.max(1, page - 1) : page;
            }
            if (this.selectedPageType === 'even') {
                if (page <= 1) return 1;
                return page % 2 === 0 ? page : page - 1;
            }
            return page;
        },
        applyRemotePage(page) {
            // NaN guard first. leaderPage() is null whenever there is no peer yet,
            // and the re-arm watchers fire constantly during a reload. Without this,
            // Math.round(null) chains through to a NaN currentPage and the watcher
            // then breaks follow on NaN !== NaN.
            if (!Number.isFinite(page)) return;
            if (!this.followUid) return;
            if (this.projectVersion) return;
            // Not in a PDF view: stay armed, just don't apply. This check must come
            // before the editing check — someone editing cells in list view is not
            // having a page arrive at them.
            if (!this.isPdfView) return;
            if (!this.loaded || !this.totalPages) return;
            if (isActivelyEditing()) {
                this.pauseFollow();
                return;
            }
            const rounded = Math.round(page);
            // The leader can be on a page this document doesn't have — they may
            // have pages we don't, or a page-op shrank ours. We still clamp (there
            // is nothing better to show), but clamping SILENTLY would leave the
            // follower parked on p20 believing they're synced to the leader's p40.
            // Being quietly wrong is the worst outcome this feature can produce,
            // so record it and let the toolbar say so.
            this.followOutOfRange = rounded > this.totalPages ? rounded : null;
            const clamped = this.alignToSpread(
                Math.min(Math.max(rounded, 1), this.totalPages)
            );
            if (this.followPaused) {
                this.followPaused = false;
            }
            // Clamp and align BEFORE claiming: a leader on p40 in a 20-page document
            // would otherwise claim 40, land on 20, and break itself.
            if (clamped === this.currentPage) return;
            this.setCurrentPageProgrammatic(clamped);
        },
        // Suppressed rather than torn down. The user is mid-keystroke or mid-drag;
        // they did not ask to stop following, and the leader moving is not their
        // action. onEditingMaybeEnded catches them up when they let go.
        pauseFollow() {
            this.followPaused = true;
        },
        // A pause is only useful if something ends it. The leader may not turn
        // another page for minutes, so we can't wait for the next onPeers — watch
        // for the edit finishing and re-apply the leader's LATEST page then.
        //
        // Debounced, NOT immediate. Tabbing or clicking from one field to the next
        // fires focusout on the old field, and for a few milliseconds — before the
        // new field takes focus — document.activeElement is the page body, i.e.
        // "not editing". Checking on the next tick catches exactly that gap and
        // resumes mid-edit. Waiting a beat lets the next field take focus; each
        // field-to-field move resets the timer, so the resume only fires once the
        // user has genuinely stopped moving between fields.
        onEditingMaybeEnded() {
            if (!this.followPaused) return;
            if (this.followResumeTimer) clearTimeout(this.followResumeTimer);
            // Must exceed editingGuard's FOCUS_GRACE_MS (500). Both this timer and
            // the grace are (re)armed by the same focusout, so if this fired first
            // isActivelyEditing() would still be true from the grace and we'd never
            // resume. 700 clears the grace with margin.
            this.followResumeTimer = setTimeout(() => {
                this.followResumeTimer = null;
                if (this.followPaused && !isActivelyEditing()) {
                    this.applyRemotePage(this.leaderPage());
                }
            }, 700);
        },
        breakFollow(reason) {
            if (!this.followUid) return;
            this.followUid = null;
            this.followPaused = false;
            this.followOutOfRange = null;
            this.notifyFollowChange(`Mirror off: ${reason}`);
        },
        setFollowUid(uid) {
            this.followUid = uid || null;
            this.followPaused = false;
            this.followOutOfRange = null;
            if (!uid) return;
            this.applyRemotePage(this.leaderPage());
        },
        notifyFollowChange(message) {
            if (this.$toast && this.$toast.warning) {
                this.$toast.warning(message, { timeout: 6000 });
            }
        },
        startPageFollow() {
            if (this.pfHandle) return;
            const me = firebase.auth().currentUser;
            if (!me || !this.projectID) return;
            if (this.projectVersion) return;
            this.pfHandle = startPageFollowSession({
                pid: this.projectID,
                uid: me.uid,
                getPage: () => this.currentPage,
                getCueId: () => null,
                getFollowUid: () => this.followUid,
                onPeers: (peers) => {
                    this.pageFollowPeers = peers;
                    this.applyRemotePage(this.leaderPage());
                }
            });
            this.pfHandle.publishPage();
        },
        stopPageFollow() {
            if (this.pfHandle) {
                this.pfHandle.stop();
                this.pfHandle = null;
            }
            this.followUid = null;
            this.followPaused = false;
            this.followOutOfRange = null;
            this.pageFollowPeers = {};
        },
        getPageWidth(width) {
            this.pageWidth = width;
        },
        getPageHeight(height) {
            this.pageHeight = height;
        },
        initDocument: function(pageNumber, opts) {
            if (this.documentUrl) {
                // cacheBust: the page-ops backend overwrites the PDF at the same URL,
                // so append a throwaway param to force a fresh fetch past the browser
                // cache. Firebase Storage ignores unknown query params (token stays valid).
                const src = opts && opts.cacheBust ? `${this.documentUrl}&v=${Date.now()}` : this.documentUrl;
                return this.loadDocument(src).then(async (pdfDoc) => {
                    const pdfPageProms = [];
                    this.pdfDoc = pdfDoc;
                    this.docLoaded = true;
                            
                    for (let i = 1; i < pdfDoc.numPages + 1; i++) {
                        const prom = pdfDoc.getPage(i);
                        pdfPageProms.push(prom);
                    }
                    const projectPages = await Promise.all(pdfPageProms.map((pdfPageProm, pageIndex) => pdfPageProm.then(page => page.getTextContent({normalizeWhitespace: true}).then(textContent => ({
                        pageNumber: pageIndex + 1,
                        pdfPage: page,
                        textContent: textContent.items.map(({str}) => str).join('').split('  ').join('\n')
                    })))));
                    this.projectPages = projectPages;

                    this.loaded = true;
                    this.$store.commit('updateProjectProps', {
                        pages: projectPages
                    });
                    if (pageNumber) {
                        this.setCurrentPageProgrammatic(pageNumber);
                    }
                    
                    // Never from a saved version's PDF: renamePdfPages writes the LIVE
                    // labels, and trimming them to a shorter version cut pages off the
                    // live script at the next page op (7 Oct 2026, lib/page-labels.js).
                    // Never while a page change is running (14 Oct 2026): the PDF has
                    // already changed and the labels have not, on purpose, until the
                    // change finishes. This person's own change sets
                    // showThumbnailPageUpdate; anyone's sets the page-change lock.
                    const pageChangeInFlight = Boolean(this.showThumbnailPageUpdate || this.projectBannerDisplay);
                    const labelsSeen = this.project && this.project.id ? this.project.pdfPageNumbers : undefined;
                    const healedLabels = this.project && this.project.id && healPageLabels(labelsSeen, projectPages.length, {
                        viewingVersion: Boolean(this.version || this.projectVersion),
                        pageChangeInFlight
                    });
                    if (healedLabels) {
                        this.$store.dispatch('healPdfPageLabels', {
                            expected: labelsSeen,
                            labels: healedLabels
                        }).catch(e => console.error('[page-labels] repair failed:', e && e.message)).then(() => {
                            this.working = false;
                        });
                    }
                    // Self-repair on open (Josh, 7 Oct 2026): a live list SHORTER than the
                    // PDF is extended, so page ops are not refused. Never shortens, never in
                    // a version view; the action re-reads in a transaction so two open tabs
                    // cannot extend it twice.
                    const extendedLabels = !healedLabels && this.project && this.project.id && extendedPageLabels(this.project.pdfPageNumbers, projectPages.length, {
                        viewingVersion: Boolean(this.version || this.projectVersion),
                        pageChangeInFlight
                    });
                    if (extendedLabels) {
                        this.$store.dispatch('extendPdfPageLabels', {
                            expectedLength: this.project.pdfPageNumbers.length,
                            labels: extendedLabels
                        }).catch(e => console.error('[page-labels] self-repair failed:', e && e.message));
                    }
                    if (this.showCuesLayerIDs === null) {
                        this.$store.dispatch('defaultShowCues', this.visibleLayerIDs);
                        if (this.currentLayer) {
                            this.$store.dispatch('setEditingLayer', {
                                layer: this.currentLayer,
                                type: 'cue'
                            });
                        }
                    } else {
                        const viewingToSeed = viewingToSeedOnOpen((this.$store.getters.userProjectData || {}).layers);
                        if (viewingToSeed) {
                            this.$store.dispatch('showLayerAll', viewingToSeed);
                        }
                    }
                    this.$store.dispatch('setLoader', {
                        type: false
                    });
                });
            }
            console.error('[project] leaving: could not init document (no documentUrl)', {
                projectId: this.project && this.project.id
            });
            this.$router.replace('/dashboard');
            return Promise.reject(new Error('Could not init document: documentUrl is not available'));
        },
        loadDocument: function(documentSrc) {
            this.pdfDoc = null;

            const loadingTask = PDFJS.getDocument({
                url: documentSrc,
                rangeChunkSize: 65536,
                disableStream: false,
                disableAutoFetch: true,
                workerSrc: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js'
            });
            return loadingTask;
        },
        // `reason` is not decoration: every call site already passes one
        // ('version', 'setActiveProject', 'reloadCurrentProject'), and until now
        // the signature took a single argument and silently discarded it. It is
        // now used in the give-up log, so the next time this stalls the console
        // says WHICH path stalled instead of leaving it to be re-derived.
        reload: function(newPagePosition, reason, attempt) {
            // Number() guards the historic call sites: before this change the
            // second argument was a reason string, and if one ever lands in the
            // attempt slot, NaN || 0 keeps the counter honest rather than
            // comparing a string to a number and never terminating.
            const tries = Number(attempt) || 0;
            this.loaded = false;
            this.$nextTick(() => {
                requestAnimationFrame(() => {
                    const documentUrl = this.$store.getters.documentUrl || this.documentUrl;
                    if (documentUrl) {
                        this.$store.dispatch('setLoader', { type: true });
                        const page = newPagePosition || 1;
                        this.initDocument(page).then(() => {
                            this.$store.dispatch('setLoader', { type: false });
                        }).catch((error) => {
                            console.error('[project] leaving: error initializing document', {
                                projectId: this.project && this.project.id, reason, error
                            });
                            this.$store.dispatch('setLoader', { type: false });
                            this.$router.replace('/dashboard');
                        });
                        return;
                    }

                    // No documentUrl YET. That is normal for a beat: this runs
                    // from inside nested vuexfire bind callbacks (route enter →
                    // setActiveProject → useVersion → here), and `documentUrl`
                    // reads '' whenever activeProject is momentarily unbound.
                    //
                    // It used to retry on requestAnimationFrame with NO cap, NO
                    // delay and NO failure state, re-entering ~60x a second and
                    // setting loaded = false on every pass. A transient empty URL
                    // therefore presented as the whole project reloading forever,
                    // for as long as the transient lasted -- and cleared by itself
                    // the moment it resolved, which is exactly what was reported
                    // on 10 Sept 2026 ("The Poisoner", opening a previous version).
                    //
                    // Bounded and slowed: ~4s of real waiting instead of a spin,
                    // then it stops and SAYS so rather than failing invisibly.
                    if (tries >= RELOAD_MAX_ATTEMPTS) {
                        console.error(
                            `reload(${reason || 'unknown'}): no documentUrl after ` +
                            `${tries} attempts (~${(RELOAD_MAX_ATTEMPTS * RELOAD_RETRY_MS) / 1000}s). ` +
                            'Giving up rather than looping.'
                        );
                        this.loaded = true;
                        this.$store.dispatch('setLoader', { type: false });
                        if (this.$toast && this.$toast.warning) {
                            this.$toast.warning(
                                'This project could not finish loading. Please refresh the page.',
                                { timeout: 8000 }
                            );
                        }
                        return;
                    }
                    setTimeout(() => {
                        this.reload(newPagePosition, reason, tries + 1);
                    }, RELOAD_RETRY_MS);
                });
            });
        },
        // Refresh the PDF for THIS viewer after another collaborator finished a
        // page-op. Preserves the current page and keeps the current view on error
        // (instead of reload()'s eject-to-dashboard) so a transient failure never
        // kicks viewers out mid-test.
        refreshPdfAfterRemotePageOp: function() {
            if (!this.loaded || !this.documentUrl) {
                return;
            }
            const page = Math.min(this.currentPage || 1, this.totalPages || 1) || 1;
            this.loaded = false;
            this.$store.dispatch('setLoader', { type: true });
            this.initDocument(page, { cacheBust: true })
                .then(() => {
                    this.$store.dispatch('setLoader', { type: false });
                    // Safety net: the reload can overlap the projectBannerDisplay watcher
                    // (esp. under churn) and strand the "please wait" banner open. If the
                    // op is already done, force it closed.
                    this.$nextTick(() => {
                        if (!this.projectBannerDisplay && this.$refs.bannerDialog) {
                            this.$refs.bannerDialog.close();
                        }
                    });
                })
                .catch(() => {
                    this.loaded = true;
                    this.$store.dispatch('setLoader', { type: false });
                });
        },
        getLockedStatus: function() {
            if (this.project.isSample) {
                return false;
            }
            return this.isLocked || !this.activeProjectEntitled;
        },
        toggleListView: function() {
            this.listView = !this.listView;
        },
        switchViewToPage: function(page) {
            // In split view both panes are already visible; just move the PDF page
            // (do NOT toggle the full-screen list overlay).
            if (this.inSplitView) {
                this.gotoPage(page);
                return;
            }
            this.toggleListView();
            this.gotoPage(page);
        },
        onSplitDragStart: function(e) {
            this.splitDragging = true;
            const move = (ev) => {
                const viewer = this.$el.querySelector('.page-viewer.split-view');
                if (!viewer) return;
                const rect = viewer.getBoundingClientRect();
                const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX;
                let frac = (clientX - rect.left) / rect.width;
                frac = Math.min(0.7, Math.max(0.3, frac));
                this.$store.dispatch('setSplitRatio', { value: frac });
            };
            const up = () => {
                this.splitDragging = false;
                document.removeEventListener('mousemove', move);
                document.removeEventListener('mouseup', up);
                document.removeEventListener('touchmove', move);
                document.removeEventListener('touchend', up);
            };
            document.addEventListener('mousemove', move);
            document.addEventListener('mouseup', up);
            document.addEventListener('touchmove', move, { passive: true });
            document.addEventListener('touchend', up);
            e.preventDefault();
        },
        updateHistory(data) {
            this.historyIndex = data.historyIndex;
        },
        updatePageHistory(data) {
            const next = pushStep(this.history, this.historyIndex, data);
            this.history = next.history;
            this.historyIndex = next.historyIndex;
        },
        handleKeyDown(e) {
            if (e.ctrlKey && e.altKey && e.code === 'KeyL') {
                this.listView = true;
                this.bookmarkNavOpened = false;
                e.preventDefault();
            }
            if (e.ctrlKey && e.altKey && e.code === 'KeyC') {
                this.listView = false;
                e.preventDefault();
            }
            const typing = isTypingTarget(document.activeElement);
            if ((e.ctrlKey || e.metaKey) && e.key === 'c' && this.selectedCue && !typing
                && !(window.getSelection && String(window.getSelection()))) {
                this.copySelectedCue();
                return;
            }
            if ((e.ctrlKey || e.metaKey) && e.key === 'c' && this.selectedMark && !typing
                && !(window.getSelection && String(window.getSelection()))) {
                this.copySelectedMark();
                return;
            }
            if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
                if (this.copiedCue) {
                    if (!typing) this.pasteCopiedCue();
                    return;
                }
                if (this.copiedMark) {
                    if (!typing) this.pasteCopiedMark();
                    return;
                }
                setTimeout(() => {
                    this.pasteCueNote(e);
                }, 100);
            }
        },
        handleClick(e) {
            if (e.type === 'pointerdown') {
                if (!isCuePress(e.target)) this.selectedCue = null;
                if (!isMarkPress(e.target)) this.selectedMark = null;
                const spot = pasteSpotFrom(e);
                if (spot) this.lastPasteSpot = spot;
            }
            const qlAppElement = document.getElementById('ql-app');
            if (e.target.classList.contains('ql-cue-layer-func-back') && e.target.classList.contains('rightEdge') && this.showMarginView) {
                qlAppElement.style.overflowX = 'auto';
                e.target.classList.contains('textLayer');
                // In split view the PDF occupies only its pane, so scroll by the
                // pane width, not the whole split viewer (which would overscroll).
                const measured = this.inSplitView
                    ? (document.querySelector('.page-viewer.split-view .ql-page-layout') || document.querySelector('.page-viewer'))
                    : document.querySelector('.page-viewer');
                const rect = measured.getBoundingClientRect();
                const width = rect.width;
                qlAppElement.scrollLeft += width;
            } else {
                qlAppElement.style.overflowX = '';
            }
        },
        // Drive the thumbnail grid from its own page control. Goes straight to
        // the child rather than through currentPage, so it cannot leak into Page
        // Mirror and move other people's views.
        goToThumbnailPage(page) {
            this.thumbnailPage = page;
            const grid = this.$refs.thumbnailPages;
            if (grid && grid.goToPage) {
                grid.goToPage(page);
            }
        },
        thumbnailSelectionClick(selectedIdx) {
            this.selectedPageRenameIndices = selectedIdx;
            if (this.selectedPageRenameIndices.length > 0) {
                this.setCurrentPage(this.selectedPageRenameIndices[0]);
            }
        },
        pasteCueNote() {
            const spot = resolvePasteSpot(this.lastPasteSpot, document);
            if (this.copiedElement != null && spot) {
                this.$nextTick(() => {
                    const targetElement = spot.target.closest('.ql-pages-inner');
                    if (!targetElement) return;
                    this.eventX = spot.clientX;
                    this.eventY = spot.clientY;
                    const canvas = targetElement.querySelector('.canvasWrapper canvas');
                    const bounds = canvas.getBoundingClientRect();
                    let finalPasteX = (this.eventX - bounds.x) / this.selectedScale;
                    let finalPasteY = (this.eventY - bounds.y) / this.selectedScale;
                    if (this.copiedElement.type === 'text') {
                        finalPasteX = (this.eventX - bounds.x);
                        finalPasteY = (this.eventY - bounds.y);
                        if ((finalPasteX + (100 * this.selectedScale)) > this.pageWidth) {
                            finalPasteX -= ((finalPasteX + (100 * this.selectedScale)) - this.pageWidth);
                        }
                        if ((finalPasteY + (24 * this.selectedScale)) > this.pageHeight) {
                            finalPasteY -= ((finalPasteY + (24 * this.selectedScale)) - this.pageHeight);
                        }
                        finalPasteX /= this.pageWidth;
                        finalPasteY /= this.pageHeight;
                    }
                    if (this.copiedElement.type === 'image') {
                        finalPasteX = (this.eventX - bounds.x);
                        finalPasteY = (this.eventY - bounds.y);
                        if ((finalPasteX + (120 * this.selectedScale)) > this.pageWidth) {
                            finalPasteX -= ((finalPasteX + (120 * this.selectedScale)) - this.pageWidth);
                        }
                        if ((finalPasteY + (120 * this.selectedScale)) > this.pageHeight) {
                            finalPasteY -= ((finalPasteY + (120 * this.selectedScale)) - this.pageHeight);
                        }
                        finalPasteX /= this.pageWidth;
                        finalPasteY /= this.pageHeight;
                    }
                    if (this.copiedElement.type === 'free_drawing') {
                        const freeDrawingWidth = this.copiedElement.width;
                        if (((this.eventX - bounds.x) + freeDrawingWidth) > this.pageWidth) {
                            finalPasteX -= ((finalPasteX + freeDrawingWidth) - this.pageWidth);
                        }
                        const freeDrawingHeight = this.copiedElement.height;
                        if (((this.eventY - bounds.y) + freeDrawingHeight) > this.pageHeight) {
                            finalPasteY -= ((finalPasteY + freeDrawingHeight) - this.pageHeight);
                        }
                    }
                    if (this.copiedElement.type === 'line') {
                        const lineWidth = 300 * this.selectedScale;
                        finalPasteX = (this.eventX - bounds.x);
                        if (((this.eventX - bounds.x) + lineWidth) > this.pageWidth) {
                            finalPasteX -= ((finalPasteX + lineWidth) - this.pageWidth);
                        }
                        finalPasteX /= this.selectedScale;
                    }
                    if (this.copiedElement.type === 'highlight') {
                        const highlightWidth = this.copiedElement.width;
                        if (((this.eventX - bounds.x) + highlightWidth) > this.pageWidth) {
                            finalPasteX -= ((finalPasteX + highlightWidth) - this.pageWidth);
                        }
                        const highlightHeight = this.copiedElement.height;
                        if (((this.eventY - bounds.y) + highlightHeight) > this.pageHeight) {
                            finalPasteY -= ((finalPasteY + highlightHeight) - this.pageHeight);
                        }
                    }

                    if (this.copiedElement.type === 'stamp') {
                        const minStamp = (this.currentNoteLayer.stampSize / 2);
                        let stampDefualtWidth = (((this.eventX - bounds.x) / this.selectedScale));
                        let stampDefualtHeight = (((this.eventY - bounds.y) / this.selectedScale));
                        const differnceWidth = (this.pageWidth / this.selectedScale) - stampDefualtWidth;
                        const differnceHeight = (this.pageHeight / this.selectedScale) - stampDefualtHeight;
                        if (stampDefualtWidth <= minStamp) {
                            stampDefualtWidth += (minStamp - stampDefualtWidth) + 10;
                        }
                        if (stampDefualtHeight <= minStamp) {
                            stampDefualtHeight += (minStamp - stampDefualtHeight) + 10;
                        }
                        if (differnceWidth <= minStamp) {
                            if (differnceWidth < 6) {
                                stampDefualtWidth -= minStamp;
                            } else if (differnceWidth < 10) {
                                stampDefualtWidth -= (differnceWidth * (this.selectedScale * 3));
                            } else if (differnceWidth < 15) {
                                stampDefualtWidth -= (differnceWidth * (this.selectedScale * 1.5));
                            } else {
                                stampDefualtWidth -= (differnceWidth);
                            }
                        }
                        if (differnceHeight <= minStamp) {
                            if (differnceHeight < 5) {
                                stampDefualtHeight -= minStamp;
                            } else if (differnceHeight < 10) {
                                stampDefualtHeight -= (differnceHeight * (this.selectedScale * 2));
                            } else if (differnceHeight < 15) {
                                stampDefualtHeight -= (differnceHeight * this.selectedScale);
                            } else {
                                stampDefualtHeight -= (differnceHeight);
                            }
                        }

                        finalPasteX = stampDefualtWidth - minStamp;
                        finalPasteY = stampDefualtHeight - minStamp;
                    }
                    let pageNumber = this.currentPage;
                    if (targetElement) {
                        const id = targetElement.id;
                        if (id && typeof id === 'string' && id.length <= 256) {
                            const digitMatch = id.match(/(\d{1,10})$/);
                            if (digitMatch && digitMatch[1]) {
                                pageNumber = Number.parseInt(digitMatch[1], 10);
                            }
                        }
                    }
                    const note = {
                        width: this.copiedElement.width,
                        height: this.copiedElement.height,
                        x: finalPasteX,
                        y: finalPasteY,
                        text: this.copiedElement.text,
                        type: this.copiedElement.type,
                        subType: this.copiedElement.subType,
                        pageNumber: pageNumber,
                        italic: this.copiedElement.italic,
                        color2: this.copiedElement.color2,
                        opacity: this.copiedElement.opacity,
                        color: this.copiedElement.color,
                        lineThikness: this.copiedElement.lineThikness,
                        size: this.copiedElement.size,
                        font: this.copiedElement.font,
                        bold: this.copiedElement.bold,
                        stampSize: this.copiedElement.stampSize,
                        coords: this.copiedElement.coords
                    };
                    this.$store.dispatch('addUserNote', {
                        note,
                        layer: this.currentNoteLayer
                    }).then((noteData) => {
                        const noteText = {
                            ...this.copiedElement,
                            id: noteData.id,
                            pageNumber: pageNumber,
                            historyType: 'add',
                            text: this.copiedElement.text,
                            subType: this.copiedElement.subType,
                            toText: this.copiedElement.text
                        };
                        this.updatePageHistory(noteText);
                    });
                });
            }
        },
        updateScroll: function() {
            this.animationFrame = requestAnimationFrame(this.updateScroll);
        },
        updateProjectBannerDisplay() {
            const url = `https://us-central1-${process.env.VUE_APP_FIREBASE_PROJECTID}.cloudfunctions.net/updateProjectBannerDisplay`;
            const postData = { id: this.projectID};
            axios.post(url, postData)
                .then(() => {
                })
                .catch((error) => {
                    console.error('Error updating project banner display:', error);
                });
        },
        async handleBeforeUnload(event) {
            try {
                if (!this.projectBannerDisplay) {
                    return undefined;
                }

                const message = 'Are you sure you want to leave?';
                event.preventDefault();
                event.returnValue = message;

                await this.updateProjectBannerDisplay();
                
                this.unloadTimer = setTimeout(() => {
                    db.collection('projects').doc(this.projectID).update({
                        projectBannerDisplay: true
                    }).catch(err => console.error('Backup update failed:', err));
                }, 1000);

                return message;
            } catch (error) {
                console.error('Failed to update project banner display:', error);
                event.preventDefault();
                event.returnValue = 'Are you sure you want to leave?';
                return event.returnValue;
            }
        },
        async closeAlert() {
            await this.$store.dispatch('projectBannerDisplay', { projectBannerDisplay: false });
        }
    },
    components: {
        'core-nav-main': CoreNavMain,
        'core-header': CoreHeader,
        'core-footer': CoreFooter,
        'project-header': ProjectHeader,
        'project-sub-header': ProjectSubHeader,
        'project-bookmark-nav': ProjectBookmarkNav,
        'project-worknote-cues-nav': WorkNoteCuesNav,
        'project-pages': ProjectPages,
        'project-thumbail-pages': ProjectThumbnailPages,
        'project-list-view': ProjectListView,
        'use-show-pass': UseShowPass
    },
    beforeRouteEnter: function(to, from, next) {
        next((vm) => {
            const currentUser = firebase.auth().currentUser;
            if (!currentUser) {
                vm.$router.replace('/login');
                return;
            }
            vm.$store.dispatch('setActiveUser', {
                ref: db.collection('users').doc(currentUser.uid),
                callback: () => { vm.readyUser = true; }
            });
            vm.$store.dispatch('setActiveProject', {
                ref: db.collection('projects').doc(vm.id),
                callback: () => {
                    vm.readyProject = true;
                    const initialPage = vm.resolveInitialPageFromRoute();
                    if (vm.version) {
                        vm.$store.dispatch('useVersion', {
                            versionID: vm.version || null,
                            callback: () => {
                                vm.reload(initialPage, 'version');
                            }
                        });
                    } else {
                        // A deep link (?cue= / ?page=) wins; otherwise reopen on the
                        // page this device last left the live doc on, not always page 1.
                        const q = (vm.$route && vm.$route.query) || {};
                        const hasDeepLink = Boolean(q.cue || q.page);
                        vm.reload(hasDeepLink ? initialPage : vm.rememberedPage(), 'setActiveProject');
                    }
                }
            });

            vm.$store.dispatch('setInvitationsRef', {
                ref: db.collection('invitations').where('pid', '==', vm.id),
                callback: () => {}
            });
        });
    },
    created: function() {
        this.theme = this.showMode ? 'show-mode' : 'default';
        Vue.material.setCurrentTheme(this.theme);

        NavBus.$on('goToPage', (e) => {
            this.currentPage = e;
        });
        NavBus.$on('reloadCurrentProject', ({ newPagePosition }) => {
            this.reload(newPagePosition, 'NavBus:reloadCurrentProject');
        });
        NavBus.$on('cue-selected', this.onCueSelected);
        NavBus.$on('structure-mark-selected', this.onMarkSelected);
    },
    mounted: function() {
        document.addEventListener('keydown', this.handleKeyDown, { passive: true });
        document.addEventListener('click', this.handleClick, { passive: true });
        document.addEventListener('pointerdown', this.handleClick, { passive: true });
        this.animationFrame = requestAnimationFrame(this.updateScroll);
        window.addEventListener('beforeunload', this.handleBeforeUnload);
        initEditingGuard();
        document.addEventListener('focusout', this.onEditingMaybeEnded, true);
        document.addEventListener('pointerup', this.onEditingMaybeEnded, true);
        document.addEventListener('click', this.onEditingMaybeEnded, true);
        this.$nextTick(() => this.handleInboundDeepLink());
    },
    beforeDestroy: function() {
        NavBus.$off('goToPage');
        NavBus.$off('reloadCurrentProject');
        NavBus.$off('cue-selected', this.onCueSelected);
        NavBus.$off('structure-mark-selected', this.onMarkSelected);
        document.removeEventListener('keydown', this.handleKeyDown);
        document.removeEventListener('click', this.handleClick);
        document.removeEventListener('pointerdown', this.handleClick);
        cancelAnimationFrame(this.animationFrame);
        this.$store.commit('unsetActiveProject');
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
        // stop() is idempotent, which matters: teardown is driven from here AND
        // from the projectVersion watcher, and it is not certain which runs when
        // the route changes between live and a saved version.
        document.removeEventListener('focusout', this.onEditingMaybeEnded, true);
        document.removeEventListener('pointerup', this.onEditingMaybeEnded, true);
        document.removeEventListener('click', this.onEditingMaybeEnded, true);
        if (this.followResumeTimer) clearTimeout(this.followResumeTimer);
        this.stopPageFollow();
        teardownEditingGuard();
    }
};

export default ProjectView;
</script>

<style lang="scss">
  
    #ql-app {
        .header-main{
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            z-index: 1000;
        }
    }
    .page-viewer {
        position: relative;
        background-color: #f0eded;
        padding: 5px 70px;
    }
    .md-theme-show-mode .page-viewer {
        background-color: #424242;
    }

    // --- Split View: PDF pane (left) beside the editable list (right) ---
    .page-viewer.split-view {
        display: flex;
        flex-wrap: nowrap !important; // vue-material md-layout defaults to wrap
        align-items: stretch;
        padding: 5px 8px;

        > .ql-pages-container {
            // Fixed basis (not 1 1 auto) so the list never reflows to fill the
            // space when project-pages briefly unmounts on a zoom reload.
            flex: 0 0 calc(var(--pdf-frac, 0.55) * 100% - 5px);
            max-width: calc(var(--pdf-frac, 0.55) * 100% - 5px);
            overflow: auto;
        }

        .ql-split-divider {
            flex: 0 0 10px;
            cursor: col-resize;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #e0dede;
            z-index: 20;

            .ql-split-grip {
                width: 2px;
                height: 44px;
                background: #999;
                border-radius: 1px;
            }
        }

        // Override list-view's full-screen fixed overlay into an in-flow pane.
        // The inner .ql-project-list-view-content keeps overflow:auto (the scroller
        // the virtualization + page sync read), so overflow stays OFF the outer.
        // Fixed basis keeps the list pane put while the PDF pane reloads on zoom.
        .ql-project-list-view {
            position: relative;
            top: 0;
            left: 0;
            flex: 0 0 calc((1 - var(--pdf-frac, 0.55)) * 100% - 5px);
            max-width: calc((1 - var(--pdf-frac, 0.55)) * 100% - 5px);
            width: auto;
            height: auto;
            overflow: hidden;

            // max-height: none, NOT merely "no max-height here". The base rule
            // (list-view.vue:1774) sets `max-height: calc(100vh - 47px)` for the
            // full-screen list, and simply dropping the split-view override just
            // swaps one viewport cap for a slightly larger one. Measured at 125%
            // zoom: viewport 875, so the base cap resolved to 828px while the
            // split container was 1008px -- the pane came up 180px short and six
            // cues showed below it. At 100% the container happened to be 810px,
            // under the cap, which is why the first attempt looked correct.
            max-height: none;

            // NO viewport-relative cap on the pane itself. This element is what
            // HIDES the PDF pane's cues: it is z-index 19 with an opaque
            // background, over cues at z-index 11 inside .ql-pages-container at
            // 18. Cues legitimately render OUTSIDE the PDF pane -- the pane does
            // not clip (palette/index.vue forces `overflow: unset !important` on
            // .ql-pages-container, which for a non-inherited property means
            // `visible`) -- so being covered by this pane is the only thing
            // keeping them out of the list.
            //
            // `max-height: calc(100vh - 100px)` left a strip at the bottom of the
            // split container that this pane did not cover. Measured on staging:
            // container bottom 810, pane bottom 775, and a cue spanning 773-786
            // showed its last 11px in the gap. The container's bottom does not
            // track the viewport -- the page scrolls under a fixed header -- so
            // NO viewport-relative number can match it. align-items: stretch on
            // .page-viewer.split-view now sizes this to the container exactly.
            //
            // The viewport cap belongs on the SCROLLER instead, so the list's own
            // scrollbar stays reachable when a tall PDF page makes the split
            // container taller than the screen.
            .ql-project-list-view-content {
                max-height: calc(100vh - 100px);
            }
        }

        // No cue-selection checkboxes in split view (the Auto-Number/Delete
        // toolbar they drive is hidden here).
        .ql-cell-select {
            display: none;
        }

        // Keep an expanded cue-attributes box inside the narrow PDF pane and let it
        // scroll internally instead of overflowing under the list (paired with the
        // in-pane anchoring in cue.vue's styles()).
        .ql-cue.cue-expanded {
            max-width: 44vw;

            .ql-cue-body {
                max-width: 100%;
                max-height: 60vh;
                overflow: auto;
            }
        }
    }

    .split-dragging {
        user-select: none;
        cursor: col-resize;
    }

    // Two half-width panes are both unusable on narrow screens — fall back to PDF.
    @media (max-width: 900px) {
        .page-viewer.split-view {
            > .ql-pages-container {
                flex: 1 1 100%;
                max-width: 100%;
            }
            .ql-split-divider,
            .ql-project-list-view {
                display: none;
            }
        }
    }

    .alert-banner-dialog {
    top: 20px !important;
    position: fixed !important;
    margin: 0 auto;
    width: 90%;
    max-width: 740px;
    z-index: 9999;
    border-radius: 12px;
    overflow: hidden;

    .md-dialog {
        border-radius: 10px;
    }
    .md-backdrop.md-active {
        background-color: rgba(0, 0, 0, 0.9) !important;
    }
}
.alert-banner-dialog:focus-visible {
  outline: none !important;
  box-shadow: none !important;
}

.alert-content {
  padding: 0;
}

.alert-box {
  display: flex;
  align-items: flex-start;
  padding: 1.25rem;
  margin-top: -25px;
}

.alert-icon-box {
  background-color: #73D5E3;
  border-radius: 50%;
  padding: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
}

.alert-icon {
  color: #fff;
}

.alert-text-box {
  flex: 1;
  margin-top: -15px;
}

.alert-title {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 0.4rem;
  color: #73D5E3;
}

.alert-message {
  font-size: 15px;
  color: #5d4037;
  line-height: 1.4;
}

.md-theme-show-mode {
    .alert-icon-box {
        background-color: #333333;
    }
    .alert-content {
        background-color: #424242 !important;
    }
    .alert-title {
        color: #fff;
    }
    .alert-message {
        color: #fff;
    }
}
</style>


// WEBPACK FOOTER //
// project.vue?16d4a45e